/* Falcon theme by Saar LLC (https://saar-llc.com). Copyright (c) 2026 Saar LLC. All rights reserved. */
/* Falcon — global behaviors (original code, Saar LLC). Vanilla custom elements, no dependencies. */
(() => {
  const theme = window.theme || {};
  const qs = (sel, el = document) => el.querySelector(sel);
  const qsa = (sel, el = document) => Array.from(el.querySelectorAll(sel));
  const mqDesktop = window.matchMedia('(min-width: 1024px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Money ---------------- */
  function formatMoney(cents, format = theme.settings?.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) / 100;
    const fmt = (n, dec = 2, thousands = ',', decimal = '.') => {
      const parts = n.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return dec ? parts.join(decimal) : parts[0];
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value);
      }
    });
  }
  theme.formatMoney = formatMoney;

  /* ---------------- Scroll lock ---------------- */
  let lockCount = 0;
  const lockScroll = () => { lockCount++; document.body.classList.add('scroll-lock'); };
  const unlockScroll = () => { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('scroll-lock'); };

  /* ---------------- Section rendering helper ---------------- */
  async function fetchSections(ids, url = window.location.pathname) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('sections', ids.join(','));
    const res = await fetch(u.toString());
    return res.json();
  }
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------------- Sticky header ---------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper');
      this.setupTone();
      if (!this.hasAttribute('data-sticky')) return;
      this.overlay = undefined;
      this.threshold = 0;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.measure();
      window.addEventListener('resize', () => { this.measure(); this.onScroll(); });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    /* Floating glass header: pick light or dark text/logo from what is behind it.
       Order: explicit data-header-bg on the slide/section, then the pixels of the image behind (sampled once,
       small), then the text colour the section uses, then the background colour. */
    setupTone() {
      this.headerEl = qs('.header', this);
      if (!this.headerEl || !this.headerEl.matches('[class*="header--float-glass"]')) return;
      this.lumCache = new Map();
      this.toneQueued = false;
      const queue = () => this.queueTone();
      document.addEventListener('slideshow:change', queue);
      window.addEventListener('load', queue);
      window.addEventListener('resize', queue);
      document.addEventListener('shopify:section:load', queue);
      this.queueTone();
    }
    queueTone() {
      if (!this.lumCache || this.toneQueued) return;
      this.toneQueued = true;
      requestAnimationFrame(() => { this.toneQueued = false; this.updateTone(); });
    }
    glassActive() {
      if (!this.isOverlay()) return false;
      return !this.classList.contains('is-stuck') || this.headerEl.classList.contains('header--keep-float');
    }
    updateTone() {
      if (!this.glassActive()) { this.classList.remove('tone-dark', 'tone-light'); return; }
      const r = qs('.header__inner', this).getBoundingClientRect();
      const ys = [r.top + r.height * 0.5];
      const xs = [0.12, 0.5, 0.88].map((f) => r.left + r.width * f);
      let dark = 0; let n = 0;
      xs.forEach((x) => ys.forEach((y) => {
        const lum = this.lumAt(x, y);
        if (lum === null) return;
        n++; if (lum < 0.55) dark++;
      }));
      const isDark = n === 0 ? true : dark * 2 >= n;
      this.classList.toggle('tone-dark', isDark);
      this.classList.toggle('tone-light', !isDark);
    }
    lumAt(x, y) {
      // elementsFromPoint skips pointer-events:none layers (the dark .overlay veil) and hidden slides.
      const stack = document.elementsFromPoint(x, y).filter((el) => !el.closest('.shopify-section-group-header-group'));
      for (const el of stack) {
        const hinted = el.closest('[data-header-bg]');
        if (hinted && hinted.dataset.headerBg && hinted.dataset.headerBg !== 'auto') return hinted.dataset.headerBg === 'dark' ? 0 : 1;
        const host = el.closest('.slideshow__slide, .hero-banner, .shopify-section');
        const ov = host && qs('.overlay', host);
        const ovc = ov ? this.parseColor(getComputedStyle(ov).backgroundColor) : null;
        const veil = ovc ? ovc[3] : 0;
        if (el.matches('img, video')) {
          const base = el.tagName === 'IMG' ? this.mediaLum(el, x, y) : null;
          if (base === null) return this.textHint(el);
          return base * (1 - veil);
        }
        const bg = this.parseColor(getComputedStyle(el).backgroundColor);
        if (bg && bg[3] > 0.6) return this.colorLum(bg) * (1 - veil);
      }
      const body = this.parseColor(getComputedStyle(document.body).backgroundColor);
      return body ? this.colorLum(body) : 1;
    }
    textHint(el) {
      // The section's own text colour says what the merchant designed for: light text means a dark image.
      const host = el.closest('.slideshow__slide, .hero-banner, .shopify-section');
      const txt = host && qs('.slideshow__heading, .hero-banner__heading, h1, h2, p', host);
      const c = txt ? this.parseColor(getComputedStyle(txt).color) : null;
      return c ? 1 - this.colorLum(c) : 0;
    }
    parseColor(str) {
      const m = str && str.match(/rgba?\(([^)]+)\)/);
      if (!m) return null;
      const p = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
      return [p[0], p[1], p[2], p.length > 3 ? p[3] : 1];
    }
    colorLum(c) {
      const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
      return Math.sqrt(0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]));
    }
    mediaLum(img, x, y) {
      const src = img.currentSrc || img.src;
      if (!src || (img.tagName === 'IMG' && !img.complete)) { img.addEventListener?.('load', () => this.queueTone(), { once: true }); return null; }
      let entry = this.lumCache.get(src);
      if (!entry) {
        entry = { grid: null, failed: false };
        this.lumCache.set(src, entry);
        try {
          const u = new URL(src, location.href);
          if (u.searchParams.has('width')) u.searchParams.set('width', '96');
          const probe = new Image();
          probe.crossOrigin = 'anonymous';
          probe.onload = () => {
            try {
              const c = document.createElement('canvas');
              c.width = 48; c.height = Math.max(1, Math.round(48 * probe.naturalHeight / probe.naturalWidth));
              const ctx = c.getContext('2d', { willReadFrequently: true });
              ctx.drawImage(probe, 0, 0, c.width, c.height);
              entry.grid = { w: c.width, h: c.height, d: ctx.getImageData(0, 0, c.width, c.height).data };
            } catch (e) { entry.failed = true; }
            this.queueTone();
          };
          probe.onerror = () => { entry.failed = true; this.queueTone(); };
          probe.src = u.toString();
        } catch (e) { entry.failed = true; }
        return null;
      }
      if (!entry.grid) return null;
      // Map the viewport point to the image (object-fit: cover, centred), then average a small patch.
      const r = img.getBoundingClientRect();
      const g = entry.grid;
      const scale = Math.max(r.width / g.w, r.height / g.h);
      const ox = (r.width - g.w * scale) / 2; const oy = (r.height - g.h * scale) / 2;
      const gx = Math.floor((x - r.left - ox) / scale); const gy = Math.floor((y - r.top - oy) / scale);
      let sum = 0; let cnt = 0;
      for (let dy = -2; dy <= 2; dy++) for (let dx = -3; dx <= 3; dx++) {
        const px = Math.min(g.w - 1, Math.max(0, gx + dx)); const py = Math.min(g.h - 1, Math.max(0, gy + dy));
        const i = (py * g.w + px) * 4;
        sum += this.colorLum([g.d[i], g.d[i + 1], g.d[i + 2]]); cnt++;
      }
      return sum / cnt;
    }
    measure() {
      this.headerHeight = this.offsetHeight;
      const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--abar-sticky-h')) || 0;
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight + bar}px`);
    }
    isOverlay() {
      // Overlay (transparent-over-hero) only applies while the wrapper is taken out of flow (desktop homepage).
      return !!this.wrapper && getComputedStyle(this.wrapper).position === 'absolute';
    }
    onScroll() {
      const y = window.scrollY;
      if (this.hasAttribute('data-hide-on-scroll')) {
        const last = this.lastY ?? y;
        const menuOpen = !!this.querySelector('details[open]') || document.body.classList.contains('overflow-hidden');
        if (Math.abs(y - last) > 6 || y < 10) {
          const hide = !menuOpen && y > last && y > this.headerHeight * 2 && !(window.Shopify && window.Shopify.designMode);
          this.classList.toggle('is-hidden-up', hide);
          this.lastY = y;
        }
      }
      const overlay = this.isOverlay();
      if (overlay !== this.overlay) {
        this.overlay = overlay;
        this.stuck = undefined;
        this.style.position = this.style.top = this.style.left = this.style.right = '';
      }
      const stuck = y > (overlay ? this.headerHeight : Math.max(this.wrapper.offsetTop, 0));
      if (this.lumCache && (stuck !== this.stuck || this.glassActive())) this.queueTone();
      if (stuck === this.stuck) return;
      this.stuck = stuck;
      this.classList.toggle('is-stuck', stuck);
      if (this.overlay) {
        this.style.position = stuck ? 'fixed' : '';
        this.style.top = stuck ? 'var(--abar-sticky-h, 0px)' : '';
        this.style.left = stuck ? '0' : '';
        this.style.right = stuck ? '0' : '';
        if (stuck) {
          this.animate([{ transform: 'translateY(-100%)' }, { transform: 'none' }], { duration: reduceMotion ? 0 : 450, easing: 'cubic-bezier(0.3, 1, 0.3, 1)' });
        }
      }
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------------- Desktop menu (hover-to-open details) ---------------- */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = qsa(':scope > ul > li > details', this);
      this.items.forEach((details) => {
        const li = details.parentElement;
        let closeTimer;
        const open = () => {
          clearTimeout(closeTimer);
          this.items.forEach((d) => d !== details && this.close(d));
          if (!details.open) { details.open = true; this.syncOverlay(); }
        };
        const scheduleClose = () => { closeTimer = setTimeout(() => this.close(details), 120); };
        li.addEventListener('mouseenter', () => { if (mqDesktop.matches) open(); });
        li.addEventListener('mouseleave', () => { if (mqDesktop.matches) scheduleClose(); });
        details.addEventListener('toggle', () => this.syncOverlay());
        details.addEventListener('keyup', (e) => { if (e.key === 'Escape') { this.close(details); qs('summary', details).focus(); } });
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.items.forEach((d) => this.close(d)); });
      // Sidebar mega menu: hovering a left-column link swaps the thumbnail panel
      qsa('[data-sidebar-index]', this).forEach((a) => {
        const swap = () => {
          const menu = a.closest('.mega-menu');
          qsa('[data-sidebar-panel]', menu).forEach((p) => { p.hidden = p.dataset.sidebarPanel !== a.dataset.sidebarIndex; });
          qsa('[data-sidebar-index]', menu).forEach((x) => x.classList.toggle('is-active', x === a));
        };
        a.addEventListener('mouseenter', swap);
        a.addEventListener('focus', swap);
      });
    }
    close(details) { if (details.open) { details.open = false; this.syncOverlay(); } }
    syncOverlay() {
      const anyMega = this.items.some((d) => d.open && d.querySelector('.mega-menu'));
      document.body.classList.toggle('mega-open', anyMega);
    }
  }
  customElements.define('header-menu', HeaderMenu);

  /* ---------------- Drawer / modal (native <dialog>) ---------------- */
  class ThemeDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = qs('dialog', this);
      if (!this.dialog) return;
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog || e.target.closest('[data-close]')) { this.close(); return; }
        const toggle = e.target.closest('[data-toggle]');
        if (toggle) {
          const panel = document.getElementById(toggle.dataset.toggle);
          if (panel) { panel.hidden = !panel.hidden; toggle.setAttribute('aria-expanded', String(!panel.hidden)); }
        }
      });
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
    }
    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      lockScroll();
      // Let the drawer contents glide in after the panel (CSS: .drawer.is-entering).
      if (!reduceMotion) {
        clearTimeout(this.enterTimer);
        this.dialog.classList.add('is-entering');
        this.enterTimer = setTimeout(() => this.dialog.classList.remove('is-entering'), 1300);
      }
      this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
      const focusTarget = qs('[autofocus]', this.dialog);
      if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
    }
    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      unlockScroll();
      this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
      this.opener?.focus?.({ preventScroll: true });
    }
  }
  customElements.define('theme-dialog', ThemeDialog);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (!opener) return;
    const target = document.getElementById(opener.dataset.open);
    if (!target) return;
    if (opener.dataset.open === 'CartDrawer' && theme.settings.cartType !== 'drawer') return;
    e.preventDefault();
    target.open(opener);
  });

  /* ---------------- Mobile menu drawer: sliding sub-panels ---------------- */
  class MenuDrawer extends ThemeDialog {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', (e) => {
        const next = e.target.closest('[data-panel-open]');
        const back = e.target.closest('[data-panel-back]');
        if (next) {
          const panel = document.getElementById(next.dataset.panelOpen);
          next.closest('.menu-drawer__panel').classList.add('is-behind');
          panel.classList.add('is-active');
          panel.dataset.parent = next.closest('.menu-drawer__panel').id;
          qs('[data-panel-back]', panel)?.focus({ preventScroll: true });
        } else if (back) {
          const panel = back.closest('.menu-drawer__panel');
          panel.classList.remove('is-active');
          const parent = document.getElementById(panel.dataset.parent);
          parent?.classList.remove('is-behind');
          qs(`[data-panel-open="${panel.id}"]`, parent || this)?.focus({ preventScroll: true });
        }
      });
      this.addEventListener('dialog:close', () => {
        qsa('.menu-drawer__panel', this).forEach((p) => p.classList.remove('is-active', 'is-behind'));
        const body = qs('.menu-drawer__body', this); if (body) body.scrollLeft = 0;
      });
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* ---------------- Carousel (scroll-snap + progress + arrows) ---------------- */
  class CarouselSlider extends HTMLElement {
    connectedCallback() {
      this.track = qs('.carousel__track', this);
      if (!this.track) return;
      this.prev = qs('[data-carousel-prev]', this);
      this.next = qs('[data-carousel-next]', this);
      this.bar = qs('.carousel__progress-bar', this);
      this.counter = qs('[data-carousel-current]', this);
      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
      this.setupDrag();
    }
    get slides() { return Array.from(this.track.children).filter((el) => el.offsetParent !== null); }
    step(dir) {
      const slide = this.slides[0];
      if (!slide) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + gap) / (slide.offsetWidth + gap)));
      this.track.scrollBy({ left: dir * perView * (slide.offsetWidth + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    goTo(index) {
      const slide = this.slides[index];
      if (slide) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft - parseFloat(getComputedStyle(this.track).paddingLeft || 0), behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const progress = max > 0 ? this.track.scrollLeft / max : 0;
      const sizeRatio = this.track.scrollWidth ? Math.min(1, this.track.clientWidth / this.track.scrollWidth) : 1;
      if (this.bar) {
        this.bar.style.width = `${sizeRatio * 100}%`;
        this.bar.style.transform = `translateX(${progress * (1 / sizeRatio - 1) * 100}%)`;
      }
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
      if (this.counter) {
        const first = this.slides[0];
        const step = first ? first.offsetWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0) : 1;
        this.counter.textContent = Math.min(this.slides.length, Math.round(this.track.scrollLeft / step) + 1);
      }
      this.classList.toggle('is-static', max <= 2);
      const footer = qs('.carousel__footer', this);
      footer?.classList.toggle('is-static', max <= 2);
    }
    setupDrag() {
      let down = false; let startX = 0; let startLeft = 0; let moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
        this.track.style.scrollSnapType = 'none'; this.track.style.scrollBehavior = 'auto';
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        this.track.style.scrollSnapType = ''; this.track.style.scrollBehavior = '';
      });
      this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }
  customElements.define('carousel-slider', CarouselSlider);

  /* ---------------- Smooth collapsible tabs (v1.6) ----------------
     Animates <details> open/close: height eases, content fades and slides in. Works for product
     collapsible tabs, FAQ, cart page panels, collection filters and the mobile footer menus.
     Native <details> still works without JS; reduced motion opens instantly. */
  const SMOOTH_DETAILS = 'details.accordion, details.main-cart__panel, details.facet, details.footer__details';
  const smoothEase = 'cubic-bezier(0.22, 1, 0.36, 1)';
  function animateDetails(details, open) {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    details._anim?.cancel();
    details._fade?.cancel();
    const content = [...details.children].filter((el) => el !== summary);
    const start = details.getBoundingClientRect().height;
    if (open) details.open = true;
    const style = getComputedStyle(details);
    const pad = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    const closed = summary.getBoundingClientRect().height + pad;
    const end = open ? details.getBoundingClientRect().height : closed;
    const dist = Math.abs(end - start);
    const duration = Math.min(650, Math.max(320, 260 + dist * 0.6));
    details.classList.add('is-animating');
    details.style.overflow = 'hidden';
    details._anim = details.animate({ height: [`${start}px`, `${end}px`] }, { duration, easing: smoothEase });
    if (content.length) {
      details._fade = null;
      content.forEach((el) => {
        const a = el.animate(open
          ? [{ opacity: 0, transform: 'translateY(-0.8rem)' }, { opacity: 1, transform: 'none' }]
          : [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-0.4rem)' }],
        { duration: open ? duration : duration * 0.6, easing: smoothEase, delay: open ? 60 : 0, fill: 'backwards' });
        details._fade = a;
      });
    }
    details._anim.onfinish = () => {
      if (!open) details.open = false;
      details.style.overflow = '';
      details.classList.remove('is-animating', 'is-closing');
      details._anim = null;
    };
    details._anim.oncancel = () => { details.style.overflow = ''; details.classList.remove('is-animating'); };
  }
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('summary');
    if (!summary || reduceMotion) return;
    const details = summary.parentElement;
    if (!details || !details.matches(SMOOTH_DETAILS)) return;
    if (getComputedStyle(summary).pointerEvents === 'none') return;
    e.preventDefault();
    const opening = !details.open || (details._anim && details.classList.contains('is-closing'));
    details.classList.toggle('is-closing', !opening);
    animateDetails(details, opening);
  });

  /* ---------------- Cart drawer upsell: Shopify recommendations (v1.6) ----------------
     Loads sections/cart-upsell-recommendations for a cart product, drops products already in the cart,
     and falls back to the collection cards in <template data-upsell-fallback>. Stays hidden when empty. */
  const recsCache = new Map();
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url || this.filled) return;
      this.filled = true;
      if (recsCache.has(url)) { this.fill(recsCache.get(url)); return; }
      fetch(url)
        .then((r) => (r.ok ? r.text() : ''))
        .then((text) => {
          const list = text ? qs('[data-recs-list]', parseHTML(text)) : null;
          const html = list ? list.innerHTML : '';
          recsCache.set(url, html);
          if (this.isConnected) this.fill(html);
        })
        .catch(() => this.fill(''));
    }
    fill(html) {
      const track = qs('[data-upsell-track]', this);
      if (!track) return;
      const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
      const limit = Number(this.dataset.limit) || 12;
      const seen = new Set();
      const usable = (cards) => cards.filter((c) => {
        const id = c.dataset.productId;
        if (!id || exclude.has(id) || seen.has(id)) return false;
        seen.add(id); return true;
      });
      let cards = usable(qsa('.upsell-card', parseHTML(`<ul>${html}</ul>`)));
      if (!cards.length) {
        const tpl = qs('template[data-upsell-fallback]', this);
        if (tpl) cards = usable(qsa('.upsell-card', tpl.content));
      }
      cards.slice(0, limit).forEach((c) => track.append(document.importNode(c, true)));
      this.hidden = track.children.length === 0;
    }
  }
  customElements.define('cart-recommendations', CartRecommendations);

  /* ---------------- Slideshow (fade, autoplay, counter + progress line) ---------------- */
  class SlideShow extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('.slideshow__slide', this);
      this.index = 0;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.speed = (Number(this.dataset.speed) || 5) * 1000;
      this.current = qs('[data-slide-current]', this);
      this.bar = qs('.slideshow__progress-bar', this);
      qs('[data-slide-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
      qs('[data-slide-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
      qsa('[data-slide-to]', this).forEach((b) => b.addEventListener('click', () => this.go(Number(b.dataset.slideTo), true)));
      this.setupSwipe();
      this.go(0);
      if (this.autoplay) {
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.play());
        document.addEventListener('shopify:block:select', (e) => {
          if (!this.contains(e.target)) return;
          this.pause(); this.go(this.slides.indexOf(e.target.closest('.slideshow__slide')));
        });
        this.play();
      }
    }
    go(i, user = false) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((s, k) => {
        const active = k === this.index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
        s.inert = !active;
        qsa('video', s).forEach((v) => (active ? v.play?.().catch(() => {}) : v.pause?.()));
      });
      if (this.current) this.current.textContent = this.index + 1;
      qsa('[data-slide-to]', this).forEach((b, k) => b.setAttribute('aria-current', String(k === this.index)));
      if (this.bar) this.bar.style.transform = `translateX(${this.index * 100}%)`;
      this.style.setProperty('--slide-index', this.index);
      this.dispatchEvent(new CustomEvent('slideshow:change', { bubbles: true, detail: { index: this.index } }));
      if (user && this.autoplay) this.play();
    }
    play() {
      if (!this.autoplay || reduceMotion) return;
      clearInterval(this.timer);
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }
    pause() { clearInterval(this.timer); }
    setupSwipe() {
      let x0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
        x0 = null;
      });
    }
  }
  customElements.define('slide-show', SlideShow);

  /* ---------------- Tabs ---------------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.buttons = qsa('[role="tab"]', this);
      this.panels = qsa('[role="tabpanel"]', this);
      this.buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => this.select(i));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.buttons.length) % this.buttons.length;
            this.select(next); this.buttons[next].focus();
          }
        });
      });
    }
    select(i) {
      this.buttons.forEach((b, k) => { b.setAttribute('aria-selected', String(k === i)); b.tabIndex = k === i ? 0 : -1; });
      this.panels.forEach((p, k) => {
        p.hidden = k !== i;
        if (k === i) {
          p.animate?.([{ opacity: 0, transform: 'translateY(1.5rem)' }, { opacity: 1, transform: 'none' }], { duration: reduceMotion ? 0 : 500, easing: 'cubic-bezier(0.3, 1, 0.3, 1)' });
          qs('carousel-slider', p)?.update?.();
        }
      });
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ---------------- Scroll animations (Theme settings > Animations) ---------------- */
  // <html data-rv / data-rv-img / data-rv-text> carry the merchant's choices; CSS in base.css defines each look.
  const motion = (() => {
    const html = document.documentElement;
    const contentOn = html.classList.contains('reveal-on');
    const imgMode = html.dataset.rvImg || 'none';
    const textMode = html.dataset.rvText || 'none';
    const blockMode = html.dataset.rvBlock || 'none';
    const enabled = !reduceMotion && 'IntersectionObserver' in window && (contentOn || imgMode !== 'none' || blockMode !== 'none');
    const IMG_SKIP = '.product-gallery, .slideshow, .ch, .pt-media, .testimonials-split__medias, .testimonials-split__product, .testimonials-split__avatar, dialog, .header-wrapper, .cg, .compare, .marquee-section, .collection-pills, .cart-lines, .search-card, .menu-drawer, [data-no-rv]';
    const TEXT_SKIP = '.slideshow, .product-card, dialog, .header-wrapper, .marquee-section, .ch__titles, [data-no-rv]';
    const HEADINGS = ':is(h1, h2, .h0, .h1, .display, .mc-heading, .newsletter__heading, .footer__statement)';
    // Sections that already move their own cards/media (sliders, sticky stacks, before/after) keep their own motion.
    const BLOCK_SKIP = '.product-gallery, .slideshow, .testimonials-split, .press, .ch, .pt-media, .testimonials-split__medias, .compare, .cg, .layers, .marquee-section, .collection-pills, .cart-lines, dialog, .header-wrapper, .menu-drawer, .facets, [data-no-rv]';
    const FRAMES = '.mc-media, .media, deferred-video, .video-frame';
    const done = (el) => setTimeout(() => el.classList.add('rv-done'), 2600);

    const pending = new Set();
    const reveal = (el) => {
      el.classList.add('is-revealed');
      io?.unobserve(el);
      pending.delete(el);
      done(el);
    };
    const io = enabled ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Also reveal anything already above the screen (scroll restored on reload, anchor jumps).
        if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) reveal(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }) : null;
    const watch = (el) => { pending.add(el); io.observe(el); };
    // Content skipped over in one jump (End key, scrollbar drag) never intersects: show it once scrolling stops.
    if (enabled) {
      let t;
      window.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(() => pending.forEach((el) => { if (el.getBoundingClientRect().bottom < 0) reveal(el); }), 160);
      }, { passive: true });
    }

    // Wrap every word (and optionally every letter) of a heading, keeping inline markup such as <em>, <strong> and colored <span>s.
    function split(el) {
      if (el.dataset.rvSplit || el.querySelector('a, button, input, img, svg, br + br')) return false;
      const label = el.textContent.replace(/\s+/g, ' ').trim();
      if (!label || label.length > 160) return false;
      const letters = textMode === 'letters' && label.length <= 70;
      let wi = 0, ci = 0;
      const walk = (node) => {
        [...node.childNodes].forEach((child) => {
          if (child.nodeType === 1) { walk(child); return; }
          if (child.nodeType !== 3 || !child.textContent) return;
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.append(document.createTextNode(' ')); return; }
            const w = document.createElement('span');
            w.className = 'rv-w';
            w.setAttribute('aria-hidden', 'true');
            const inner = document.createElement('span');
            inner.className = 'rv-wi';
            inner.style.setProperty('--wi', Math.min(wi++, 30));
            if (letters) {
              [...part].forEach((ch) => {
                const c = document.createElement('span');
                c.className = 'rv-c';
                c.textContent = ch;
                c.style.setProperty('--ci', Math.min(ci++, 70));
                inner.append(c);
              });
            } else inner.textContent = part;
            w.append(inner);
            frag.append(w);
          });
          child.replaceWith(frag);
        });
      };
      walk(el);
      el.setAttribute('aria-label', label);
      el.dataset.rvSplit = letters ? 'letters' : 'words';
      el.classList.add('rv-split');
      el.classList.remove('reveal');
      return true;
    }

    // Cards and media: whole cards in a row/grid move in one after another, and standalone image/video frames move as one piece.
    function blocks(root) {
      const vw = window.innerWidth;
      const box = (el) => el.getBoundingClientRect();
      const tag = (el, order) => {
        if (el.classList.contains('is-revealed')) return;
        el.classList.remove('reveal');
        el.classList.add('rv-block');
        el.style.setProperty('--rv-order', order);
        watch(el);
      };
      const scope = root === document ? qsa('main .shopify-section, .footer') : [root];
      scope.forEach((sec) => {
        // 1) Groups of similar cards: two or more same-class siblings, each at least card sized.
        qsa('*', sec).forEach((parent) => {
          if (parent.children.length < 2 || parent.closest(BLOCK_SKIP) || parent.closest('.rv-block')) return;
          const kids = [...parent.children].filter((c) => {
            if (typeof c.className !== 'string' || !c.className.trim()) return false;
            const r = box(c);
            return r.width >= 120 && r.height >= 100;
          });
          if (kids.length < 2) return;
          const key = kids[0].className.trim().split(/\s+/)[0];
          const same = kids.filter((c) => c.className.trim().split(/\s+/)[0] === key);
          if (same.length < 2 || same.length < kids.length * 0.6 || same.some((c) => c.querySelector('.rv-block'))) return;
          // Stagger by position inside each visual row.
          const rows = new Map();
          same.forEach((c) => {
            const top = Math.round(box(c).top / 40);
            const i = rows.get(top) || 0;
            rows.set(top, i + 1);
            tag(c, Math.min(i, 6));
          });
        });
        // 2) Standalone image and video frames (full-bleed banners keep only the inner image motion).
        qsa(FRAMES, sec).forEach((el) => {
          if (el.closest('.rv-block') || el.closest(BLOCK_SKIP)) return;
          const r = box(el);
          if (r.width < 240 || r.height < 160 || r.width > vw * 0.92) return;
          tag(el, 0);
        });
      });
    }

    function init(root = document) {
      if (!enabled) {
        qsa('.reveal', root).forEach((el) => el.classList.add('is-revealed'));
        return;
      }
      if (contentOn) qsa('.reveal:not(.is-revealed)', root).forEach(watch);
      if (imgMode !== 'none') {
        qsa('main .media, .footer .media', root).forEach((el) => {
          if (el.classList.contains('rv-img') || el.closest(IMG_SKIP)) return;
          if (el.getBoundingClientRect().width < 120) return;
          el.classList.add('rv-img');
          watch(el);
        });
      }
      if (blockMode !== 'none') blocks(root);
      if (contentOn && textMode !== 'none') {
        qsa(`main ${HEADINGS}, .footer ${HEADINGS}`, root).forEach((el) => {
          if (el.closest(TEXT_SKIP) || el.classList.contains('is-revealed')) return;
          if (split(el)) watch(el);
        });
      }
    }

    // Anything inserted later (AJAX filters, recommendations, quick add) is shown straight away.
    document.addEventListener('DOMContentLoaded', () => init());
    document.addEventListener('shopify:section:load', (e) => init(e.target));
    // Theme editor: a section picked in the sidebar shows fully at once.
    const revealAll = (root) => qsa('.reveal, .rv-img, .rv-split, .rv-block', root).forEach((el) => (enabled ? reveal(el) : el.classList.add('is-revealed')));
    document.addEventListener('shopify:section:select', (e) => revealAll(e.target));
    document.addEventListener('shopify:block:select', (e) => revealAll(e.target.closest('.shopify-section') || e.target));
    return { init, enabled };
  })();
  theme.motion = motion;

  /* ---------------- Smooth inertia scrolling (mouse wheel only; touch keeps native scrolling) ---------------- */
  (() => {
    const html = document.documentElement;
    if (html.dataset.smoothScroll !== 'true' || reduceMotion || window.Shopify?.designMode) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    let target = window.scrollY, current = window.scrollY, running = false;
    const max = () => document.documentElement.scrollHeight - window.innerHeight;
    // Let inner scroll areas (drawers, filters, tables) scroll natively.
    const canScrollInside = (node, dy) => {
      for (let el = node; el && el !== document.body && el !== html; el = el.parentElement) {
        if (el.scrollHeight <= el.clientHeight + 1) continue;
        const oy = getComputedStyle(el).overflowY;
        if (oy !== 'auto' && oy !== 'scroll') continue;
        if ((dy > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) || (dy < 0 && el.scrollTop > 0)) return true;
      }
      return false;
    };
    const frame = () => {
      current += (target - current) * 0.11;
      if (Math.abs(target - current) < 0.5) current = target;
      window.scrollTo({ top: current, behavior: 'instant' });
      if (current !== target) requestAnimationFrame(frame);
      else running = false;
    };
    window.addEventListener('wheel', (e) => {
      if (e.ctrlKey || e.defaultPrevented || document.body.classList.contains('scroll-lock')) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (e.target.closest?.('dialog[open], [data-native-scroll]') || canScrollInside(e.target, e.deltaY)) return;
      e.preventDefault();
      const delta = e.deltaMode === 1 ? e.deltaY * 40 : e.deltaMode === 2 ? e.deltaY * window.innerHeight : e.deltaY;
      if (!running) { current = window.scrollY; target = current; }
      target = Math.max(0, Math.min(max(), target + delta));
      if (!running) { running = true; requestAnimationFrame(frame); }
    }, { passive: false });
    // Keyboard, scrollbar and anchor jumps: resync so the next wheel starts from the real position.
    window.addEventListener('scroll', () => { if (!running) { current = target = window.scrollY; } }, { passive: true });
    html.classList.add('has-smooth-scroll');
  })();

  /* ---------------- Cart ---------------- */
  // Every cart surface (drawer, cart page) has a [data-cart-section="<section id>"] root
  // with one [data-cart-content] child that is swapped after each change.
  const CART_CORE_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const cartSectionIds = () => {
    const ids = new Set(CART_CORE_SECTIONS);
    qsa('[data-cart-section]').forEach((el) => ids.add(el.dataset.cartSection));
    return Array.from(ids);
  };
  theme.cartSectionIds = cartSectionIds;

  function updateCartBubble(html) {
    const doc = parseHTML(html);
    const count = qs('.cart-count', doc);
    const label = count?.nextElementSibling;
    qsa('.cart-count').forEach((el) => {
      el.textContent = count?.textContent.trim() || '';
      el.dataset.count = count?.dataset.count || '0';
      if (label && el.nextElementSibling?.classList.contains('visually-hidden')) el.nextElementSibling.textContent = label.textContent;
    });
  }

  function swapCartSection(id, html) {
    const fresh = qs('[data-cart-content]', parseHTML(html));
    if (!fresh) return;
    qsa(`[data-cart-section="${id}"]`).forEach((root) => {
      const current = qs('[data-cart-content]', root);
      if (!current) return;
      const scroller = qs('[data-cart-scroll]', current);
      const scrollTop = scroller ? scroller.scrollTop : 0;
      const oldBar = qs('[data-progress-bar]', current);
      const oldWidth = oldBar ? oldBar.style.width : null;
      const next = fresh.cloneNode(true);
      current.replaceWith(next);
      const nextScroller = qs('[data-cart-scroll]', next);
      if (nextScroller) nextScroller.scrollTop = scrollTop;
      const bar = qs('[data-progress-bar]', next);
      if (bar && oldWidth !== null && oldWidth !== bar.style.width) {
        const target = bar.style.width;
        bar.style.width = oldWidth;
        requestAnimationFrame(() => requestAnimationFrame(() => { bar.style.width = target; }));
      }
    });
  }

  async function refreshCartUI(sectionsData) {
    let data = sectionsData;
    if (!data) {
      const res = await fetch(`${window.location.pathname}?sections=${cartSectionIds().join(',')}`);
      data = await res.json();
    }
    Object.entries(data || {}).forEach(([id, html]) => {
      if (!html) return;
      if (id === 'cart-icon-bubble') updateCartBubble(html);
      else swapCartSection(id, html);
    });
    document.dispatchEvent(new CustomEvent('cart:refreshed'));
  }
  theme.refreshCartUI = refreshCartUI;

  async function addToCart(formData) {
    formData.append('sections', cartSectionIds().join(','));
    formData.append('sections_url', window.location.pathname);
    const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || theme.strings.cartError);
    await refreshCartUI(json.sections);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
    return json;
  }
  theme.addToCart = addToCart;

  function openCart() {
    const drawer = document.getElementById('CartDrawer');
    if (theme.settings.cartType === 'drawer' && drawer) drawer.open();
    else window.location.href = theme.routes.cart;
  }
  theme.openCart = openCart;

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-change]');
        const remove = e.target.closest('[data-remove]');
        if (btn) {
          e.preventDefault();
          const input = qs(`input[data-line="${btn.dataset.line}"]`, this);
          const qty = Math.max(0, Number(input.value) + Number(btn.dataset.qtyChange));
          input.value = qty;
          this.change(btn.dataset.line, qty);
        } else if (remove) {
          e.preventDefault();
          this.change(remove.dataset.line, 0);
        }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[data-line]');
        if (input) this.change(input.dataset.line, Math.max(0, Number(input.value) || 0));
      });
    }
    async change(line, quantity) {
      const sectionRoot = this.closest('[data-cart-section]');
      this.setAttribute('aria-busy', 'true');
      qs(`[data-line-item="${line}"]`, this)?.classList.add('is-loading');
      try {
        const res = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: cartSectionIds().join(','), sections_url: window.location.pathname })
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || theme.strings.cartError);
        await refreshCartUI(json.sections);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
      } catch (err) {
        await refreshCartUI().catch(() => {});
        const scope = sectionRoot || document;
        const msg = qs('[data-cart-error]', scope);
        if (msg) { msg.textContent = err.message; msg.hidden = false; }
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* Cart note: drawer panel (explicit save) + cart page textarea (autosave) */
  const saveCartNote = (value) => fetch(`${theme.routes.cartUpdate}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ note: value })
  }).then((r) => r.json());
  theme.saveCartNote = saveCartNote;
  let noteTimer;
  document.addEventListener('input', (e) => {
    const note = e.target.closest('[data-cart-note][data-autosave]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveCartNote(note.value), 600);
  });
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-note-open], [data-note-close], [data-note-save]');
    if (!trigger) return;
    const scope = trigger.closest('[data-cart-content]') || document;
    const panel = (trigger.getAttribute('aria-controls') && document.getElementById(trigger.getAttribute('aria-controls'))) || qs('[data-note-panel]', scope);
    if (!panel) return;
    e.preventDefault();
    const setOpen = (open) => {
      panel.classList.toggle('is-open', open);
      panel.inert = !open;
      qsa('[data-note-open]', scope).forEach((b) => { if (!panel.id || b.getAttribute('aria-controls') === panel.id) b.setAttribute('aria-expanded', String(open)); });
      if (open) setTimeout(() => qs('textarea', panel)?.focus(), 200);
    };
    if (trigger.matches('[data-note-open]')) setOpen(true);
    else if (trigger.matches('[data-note-close]')) setOpen(false);
    else {
      const area = qs('textarea', panel);
      if (!area) return;
      trigger.setAttribute('aria-busy', 'true');
      try { await saveCartNote(area.value); area.defaultValue = area.value; } finally { trigger.removeAttribute('aria-busy'); }
      qsa('[data-note-open]', scope).forEach((b) => { if (!panel.id || b.getAttribute('aria-controls') === panel.id) b.classList.toggle('has-note', !!area.value.trim()); });
      setOpen(false);
    }
  });

  /* Cart discount codes: apply / remove through /cart/update.js, then re-render the cart surfaces */
  class CartDiscount extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const apply = e.target.closest('[data-discount-apply]');
        const remove = e.target.closest('[data-discount-remove]');
        if (apply) { e.preventDefault(); this.apply(); }
        else if (remove) { e.preventDefault(); this.submit(this.codes.filter((c) => c.toLowerCase() !== remove.dataset.discountRemove.toLowerCase())); }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('[data-discount-input]')) { e.preventDefault(); this.apply(); }
      });
    }
    get codes() { return (this.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean); }
    apply() {
      const input = qs('[data-discount-input]', this);
      const code = input?.value.trim();
      if (!code) { input?.focus(); return; }
      if (this.codes.some((c) => c.toLowerCase() === code.toLowerCase())) { input.value = ''; return; }
      this.submit([...this.codes, code], code);
    }
    showError(message) {
      const error = qs('[data-discount-error]', this);
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }
    async update(codes, withSections) {
      const body = { discount: codes.join(',') };
      if (withSections) { body.sections = cartSectionIds().join(','); body.sections_url = window.location.pathname; }
      const res = await fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || theme.strings.cartError);
      return json;
    }
    async submit(codes, added) {
      this.showError('');
      this.setAttribute('aria-busy', 'true');
      const root = this.closest('[data-cart-section]');
      try {
        const json = await this.update(codes, true);
        const rejected = added && (json.discount_codes || []).some((d) => d.code.toLowerCase() === added.toLowerCase() && !d.applicable);
        if (rejected) {
          // Keep the cart clean: drop the code that can't be used, and keep the current UI (nothing changed).
          await this.update(this.codes, false).catch(() => {});
          const input = qs('[data-discount-input]', this);
          if (input) input.value = '';
          const msg = qs('[data-discount-error]', this)?.dataset.msgInvalid || theme.strings.cartError;
          this.showError(msg.replace('[code]', added));
          return;
        }
        await refreshCartUI(json.sections);
        const panel = root ? qs('[data-discount-panel]', root) : null;
        if (panel) panel.open = true;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
      } catch (err) {
        this.showError(err.message || theme.strings.cartError);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('cart-discount', CartDiscount);

  /* Cart line add/remove helpers that re-render every cart surface */
  async function cartPost(route, body) {
    const res = await fetch(`${route}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: cartSectionIds().join(','), sections_url: window.location.pathname })
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || theme.strings.cartError);
    await refreshCartUI(json.sections);
    return json;
  }

  /* Shipping protection switch in the drawer footer */
  document.addEventListener('change', async (e) => {
    const toggle = e.target.closest('[data-protection-toggle]');
    if (!toggle) return;
    const label = toggle.closest('.cart-protect');
    label?.setAttribute('aria-busy', 'true');
    toggle.disabled = true;
    try {
      const json = toggle.checked
        ? await cartPost(theme.routes.cartAdd, { items: [{ id: Number(toggle.dataset.variantId), quantity: 1 }] })
        : await cartPost(theme.routes.cartChange, { id: String(toggle.dataset.variantId), quantity: 0 });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: json }));
    } catch (err) {
      toggle.checked = !toggle.checked;
      toggle.disabled = false;
      label?.removeAttribute('aria-busy');
      const msg = qs('[data-cart-error]', toggle.closest('[data-cart-content]') || document);
      if (msg) { msg.textContent = err.message; msg.hidden = false; }
    }
  });

  /* Reward gifts: add the gift when its goal is reached, remove it when the cart drops below. */
  let giftSyncing = false;
  async function syncCartGifts() {
    if (giftSyncing) return;
    const data = qs('#CartDrawer [data-cart-gifts]');
    if (!data) return;
    let gifts = [];
    try { gifts = JSON.parse(data.textContent || '[]'); } catch (_) { return; }
    const add = gifts.filter((g) => g.reached && !g.key && g.available && g.variant)
      .map((g) => ({ id: g.variant, quantity: 1, properties: { _falcon_gift: g.goal } }));
    const remove = gifts.filter((g) => (!g.reached && g.key) || (g.key && g.qty > 1));
    if (!add.length && !remove.length) return;
    giftSyncing = true;
    try {
      for (const g of remove) await cartPost(theme.routes.cartChange, { id: g.key, quantity: g.reached ? 1 : 0 });
      if (add.length) await cartPost(theme.routes.cartAdd, { items: add });
      document.dispatchEvent(new CustomEvent('cart:gifts-synced'));
    } catch (err) {
      console.warn('Falcon: reward gift sync failed', err);
    } finally {
      giftSyncing = false;
    }
  }
  theme.syncCartGifts = syncCartGifts;
  document.addEventListener('cart:refreshed', () => { if (!giftSyncing) syncCartGifts(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncCartGifts);
  else syncCartGifts();

  /* Countdown timers: fixed date, evergreen (per visitor) or daily (until local midnight) */
  class FalconCountdown extends HTMLElement {
    connectedCallback() {
      this.units = {};
      qsa('[data-unit]', this).forEach((el) => { this.units[el.dataset.unit] = el; });
      this.end = this.resolveEnd();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    resolveEnd() {
      const mode = this.dataset.mode;
      if (mode === 'daily') { const d = new Date(); d.setHours(24, 0, 0, 0); return d.getTime(); }
      if (mode === 'evergreen') {
        const key = `falcon-cd-${this.dataset.key || 'default'}`;
        const len = (Number(this.dataset.minutes) || 15) * 60000;
        let end = 0;
        try { end = Number(localStorage.getItem(key)) || 0; } catch (_) { /* storage blocked */ }
        if (end <= Date.now()) {
          end = Date.now() + len;
          try { localStorage.setItem(key, String(end)); } catch (_) { /* storage blocked */ }
        }
        return end;
      }
      return Number(this.dataset.end || 0) * 1000;
    }
    tick() {
      let left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      if (left === 0) {
        if (this.dataset.mode === 'date') {
          clearInterval(this.timer);
          if (this.hasAttribute('data-hide-expired')) {
            (this.closest('[data-countdown-host]') || this).hidden = true;
            this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
            return;
          }
        } else {
          if (this.dataset.mode === 'evergreen') {
            try { localStorage.removeItem(`falcon-cd-${this.dataset.key || 'default'}`); } catch (_) { /* storage blocked */ }
          }
          this.end = this.resolveEnd();
          left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
        }
      }
      const pad = (n) => String(n).padStart(2, '0');
      const values = { hours: Math.floor(left / 3600), minutes: Math.floor((left % 3600) / 60), seconds: left % 60 };
      Object.entries(values).forEach(([unit, value]) => {
        const el = this.units[unit];
        const text = pad(value);
        if (el && el.textContent !== text) {
          el.textContent = text;
          if (!reduceMotion && unit === 'seconds') { el.classList.remove('is-tick'); void el.offsetWidth; el.classList.add('is-tick'); }
        }
      });
    }
  }
  if (!customElements.get('falcon-countdown')) customElements.define('falcon-countdown', FalconCountdown);

  /* Shipping rates estimator (cart page) */
  class ShippingCalculator extends HTMLElement {
    connectedCallback() {
      this.country = qs('[data-ship-country]', this);
      this.province = qs('[data-ship-province]', this);
      this.provinceWrap = qs('[data-ship-province-wrap]', this);
      this.results = qs('[data-ship-results]', this);
      const preset = this.country?.dataset.default || this.dataset.defaultCountry;
      if (this.country && preset && [...this.country.options].some((o) => o.value === preset)) this.country.value = preset;
      this.syncProvinces();
      this.country?.addEventListener('change', () => this.syncProvinces());
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-ship-calc]')) { e.preventDefault(); this.calculate(); }
      });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.matches('input')) { e.preventDefault(); this.calculate(); }
      });
    }
    syncProvinces() {
      if (!this.country || !this.province) return;
      let list = [];
      try { list = JSON.parse(this.country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) { list = []; }
      this.province.replaceChildren(...list.map(([value, label]) => new Option(label, value)));
      this.provinceWrap.hidden = list.length === 0;
    }
    show(html, isError) {
      this.results.innerHTML = '';
      const p = document.createElement('p');
      p.className = isError ? 'form-message form-message--error' : 'shipping-calc__message';
      p.textContent = html;
      this.results.append(p);
      this.results.hidden = false;
      return p;
    }
    async calculate() {
      const btn = qs('[data-ship-calc]', this);
      const params = new URLSearchParams();
      params.set('shipping_address[country]', this.country?.value || '');
      if (this.provinceWrap && !this.provinceWrap.hidden) params.set('shipping_address[province]', this.province.value);
      params.set('shipping_address[zip]', qs('[data-ship-zip]', this)?.value.trim() || '');
      const base = (theme.routes.root || '/').replace(/\/$/, '');
      btn?.setAttribute('aria-busy', 'true');
      try {
        const prep = await fetch(`${base}/cart/prepare_shipping_rates.json?${params}`, { method: 'POST', headers: { Accept: 'application/json' } });
        if (!prep.ok) {
          let detail = '';
          try { const j = await prep.json(); detail = Object.entries(j || {}).map(([k, v]) => `${k} ${[].concat(v).join(', ')}`).join('; '); } catch (_) { /* ignore */ }
          throw new Error(detail || this.dataset.msgError);
        }
        let rates = null;
        for (let i = 0; i < 12 && !rates; i++) {
          const r = await fetch(`${base}/cart/async_shipping_rates.json?${params}`, { headers: { Accept: 'application/json' } });
          if (r.ok) { const j = await r.json().catch(() => null); if (j && Array.isArray(j.shipping_rates)) rates = j.shipping_rates; }
          if (!rates) await new Promise((ok) => setTimeout(ok, 500));
        }
        if (!rates) throw new Error(this.dataset.msgError);
        if (!rates.length) { this.show(this.dataset.msgNone, true); return; }
        const head = this.show(rates.length === 1 ? this.dataset.msgOne : this.dataset.msgOther.replace('[count]', rates.length), false);
        const ul = document.createElement('ul');
        ul.className = 'shipping-calc__rates';
        ul.setAttribute('role', 'list');
        rates.forEach((rate) => {
          const li = document.createElement('li');
          const cents = Math.round(parseFloat(rate.price) * 100);
          li.textContent = `${rate.name}: ${Number.isFinite(cents) ? formatMoney(cents) : rate.price}`;
          ul.append(li);
        });
        head.after(ul);
      } catch (err) {
        this.show(err.message || this.dataset.msgError, true);
      } finally {
        btn?.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('shipping-calculator', ShippingCalculator);

  /* ---------------- Product form (add to cart) ---------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.busy) return;
      const btn = e.submitter && e.submitter.name === 'add' ? e.submitter : qs('[type="submit"][name="add"]', this.form);
      const err = qs('[data-form-error]', this);
      this.busy = true;
      if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
      if (err) err.hidden = true;
      try {
        const fd = new FormData(this.form);
        // Bundle tier with a free gift (quantity-breaks block): add the gift line in the same request.
        const gift = this.form.dataset.bundleGift;
        if (gift) {
          const plan = fd.get('selling_plan');
          const main = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) };
          if (plan) main.selling_plan = Number(plan);
          const multi = new FormData();
          multi.append('items[0][id]', main.id);
          multi.append('items[0][quantity]', main.quantity);
          if (main.selling_plan) multi.append('items[0][selling_plan]', main.selling_plan);
          multi.append('items[1][id]', gift);
          multi.append('items[1][quantity]', '1');
          multi.append('items[1][properties][_falcon_bundle_gift]', this.form.dataset.bundleTier || '1');
          await addToCart(multi);
        } else {
          await addToCart(fd);
        }
        const dialog = this.closest('theme-dialog');
        if (dialog && dialog.id !== 'CartDrawer') dialog.close();
        openCart();
      } catch (error) {
        if (err) { err.textContent = error.message; err.hidden = false; }
      } finally {
        this.busy = false;
        if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------------- Quick add button on product cards ---------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;
    const variantId = btn.dataset.variantId;
    const single = btn.dataset.singleVariant === 'true';
    const search = btn.closest('#SearchDrawer');
    btn.setAttribute('aria-busy', 'true');
    try {
      if (single && variantId) {
        const fd = new FormData();
        fd.append('id', variantId); fd.append('quantity', '1');
        await addToCart(fd);
        search?.close();
        openCart();
      } else {
        const modal = document.getElementById('QuickAddModal');
        const url = new URL(btn.dataset.productUrl, window.location.origin);
        url.searchParams.set('section_id', 'quick-add');
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(theme.strings.cartError);
        const content = qs('.quick-add__content', parseHTML(await res.text()));
        const target = qs('[data-quick-add-body]', modal);
        target.replaceChildren();
        if (content) target.append(content);
        search?.close();
        modal.open(btn);
        window.Shopify?.PaymentButton?.init?.();
      }
    } catch (err) {
      console.error(err);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  /* ---------------- Variant picker ---------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(qs('script[type="application/json"]', this)?.textContent || '[]');
      this.root = this.closest('[data-product-root]') || document;
      this.addEventListener('change', (e) => this.onChange(e));
      this.updateAvailability(this.selectedOptions());
      this.setupLooks();
    }
    // v1.7 looks: segmented thumb, dropdown with swatches, colour-filtered gallery.
    setupLooks() {
      this.segs = qsa('.vp-seg', this);
      if (this.segs.length) {
        const place = (animate) => this.segs.forEach((seg) => this.placeSeg(seg, animate));
        requestAnimationFrame(() => place(false));
        if ('ResizeObserver' in window) { this.segRO = new ResizeObserver(() => place(false)); this.segs.forEach((s) => this.segRO.observe(s)); }
        document.fonts?.ready?.then(() => place(false));
      }
      qsa('.vp-dd', this).forEach((dd) => {
        dd.addEventListener('keydown', (e) => { if (e.key === 'Escape' && dd.open) { dd.open = false; qs('summary', dd)?.focus(); } });
      });
      if (!this.ddOutside && qs('.vp-dd', this)) {
        this.ddOutside = (e) => qsa('.vp-dd[open]', this).forEach((dd) => { if (!dd.contains(e.target)) dd.open = false; });
        document.addEventListener('click', this.ddOutside);
      }
      if (this.dataset.filterMedia === 'true') requestAnimationFrame(() => this.filterMedia());
    }
    disconnectedCallback() {
      this.segRO?.disconnect();
      if (this.ddOutside) document.removeEventListener('click', this.ddOutside);
    }
    placeSeg(seg, animate) {
      const input = qs('input:checked', seg);
      const label = input && qs(`label[for="${CSS.escape(input.id)}"]`, seg);
      if (!label) return;
      seg.classList.toggle('no-anim', !animate);
      seg.style.setProperty('--seg-w', `${label.offsetWidth}px`);
      seg.style.setProperty('--seg-x', `${label.offsetLeft}px`);
      seg.classList.add('is-ready');
    }
    filterMedia() {
      const gallery = qs('product-gallery', this.root);
      if (!gallery) return;
      const ci = Number(this.dataset.colorIndex);
      const groups = qsa('fieldset', this);
      const group = groups[ci];
      if (!group) return;
      const checked = qs('input:checked', group);
      // Media ids that belong to each colour (variant images or alt-text matches).
      const owner = {};
      qsa('input[data-media-id]', group).forEach((i) => { owner[i.dataset.mediaId] = i.value; });
      this.variants.forEach((v) => { if (v.featured_media) owner[String(v.featured_media.id)] = owner[String(v.featured_media.id)] || v.options[ci]; });
      // Gallery items + thumbnails (the thumb's <li> hides so the strip has no gaps).
      const items = qsa('[data-media-id]', gallery).map((el) => (el.classList.contains('product-gallery__thumb') ? el.closest('li') || el : el));
      items.forEach((el) => { if (!el.dataset.mediaId) el.dataset.mediaId = qs('[data-media-id]', el)?.dataset.mediaId || ''; });
      const known = new Set(Object.values(owner)).size;
      if (!checked || known < 2) { items.forEach((el) => el.removeAttribute('data-color-hidden')); return; }
      // Images without an owner stay visible (shared shots); images of another colour hide.
      let shown = 0;
      items.forEach((el) => {
        const o = owner[el.dataset.mediaId];
        const hide = o !== undefined && o !== checked.value;
        el.toggleAttribute('data-color-hidden', hide);
        if (!hide && el.classList.contains('product-gallery__item')) shown += 1;
      });
      if (!shown) items.forEach((el) => el.removeAttribute('data-color-hidden'));
      // Keep a visible image active (single-image layouts).
      const active = qs('.product-gallery__item.is-active', gallery);
      if (active?.hasAttribute('data-color-hidden')) {
        const first = qsa('.product-gallery__item', gallery).find((el) => !el.hasAttribute('data-color-hidden'));
        if (first) this.root.dispatchEvent(new CustomEvent('media:select', { detail: { id: first.dataset.mediaId } }));
      }
      gallery.dispatchEvent(new CustomEvent('gallery:filtered', { bubbles: true }));
    }
    syncLooks(e) {
      const input = e?.target?.matches?.('input[type="radio"]') ? e.target : null;
      if (input) {
        const label = qs(`label[for="${CSS.escape(input.id)}"]`, this);
        if (label?.classList.contains('vp-opt') && !reduceMotion && !this.classList.contains('vp-static')) {
          label.classList.remove('is-picked'); void label.offsetWidth; label.classList.add('is-picked');
          setTimeout(() => label.classList.remove('is-picked'), 400);
        }
        const dd = input.closest('.vp-dd');
        if (dd) {
          const cur = qs('[data-dd-current]', dd); if (cur) cur.textContent = input.value;
          const media = qs('[data-dd-media]', dd);
          const sw = label && qs('.vp-swatch', label);
          if (media) media.innerHTML = sw ? sw.outerHTML : '';
          dd.open = false;
        }
      }
      this.segs?.forEach((seg) => this.placeSeg(seg, true));
      if (this.dataset.filterMedia === 'true') this.filterMedia();
      return input;
    }
    selectedOptions() {
      return qsa('fieldset, .variant-select', this).map((group) => {
        const checked = qs('input:checked', group);
        if (checked) return checked.value;
        return qs('select', group)?.value;
      });
    }
    onChange(e) {
      const changed = this.syncLooks(e);
      const opts = this.selectedOptions();
      qsa('fieldset', this).forEach((group, i) => {
        const label = qs('[data-selected-value]', group);
        if (label) label.textContent = opts[i];
      });
      const variant = this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.updateAvailability(opts);
      this.applyVariant(variant);
      // Values matched to an image by alt text (no variant image): still show that image.
      if (variant && !variant.featured_media && changed?.dataset.mediaId) {
        this.root.dispatchEvent(new CustomEvent('media:select', { detail: { id: changed.dataset.mediaId } }));
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
    updateAvailability(opts) {
      qsa('fieldset', this).forEach((group, gi) => {
        qsa('input', group).forEach((input) => {
          const candidate = opts.slice(); candidate[gi] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === candidate[i]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }
    applyVariant(variant) {
      const root = this.root;
      const own = (el) => !el.closest('.product-card');
      const addBtns = qsa('[name="add"]', root).filter(own);
      const setLabel = (btn, text) => { (qs('[data-add-label]', btn) || btn).textContent = text; };
      const dynamic = qsa('[data-dynamic-checkout]', root);
      if (!variant) {
        addBtns.forEach((b) => { b.disabled = true; setLabel(b, theme.strings.unavailable); });
        dynamic.forEach((d) => d.classList.add('is-disabled'));
        const inv = qs('[data-inventory]', root);
        if (inv) inv.hidden = true;
        return;
      }
      qsa('input[name="id"]', root).filter(own).forEach((i) => { i.value = variant.id; i.dispatchEvent(new Event('change', { bubbles: true })); });
      addBtns.forEach((b) => {
        b.disabled = !variant.available;
        setLabel(b, variant.available ? (b.dataset.labelAvailable || theme.strings.addToCart) : theme.strings.soldOut);
      });
      dynamic.forEach((d) => d.classList.toggle('is-disabled', !variant.available));
      qsa('[data-price]', root).filter(own).forEach((el) => {
        const onSale = variant.compare_at_price > variant.price;
        el.classList.toggle('price--on-sale', onSale);
        const cur = qs('.price__current', el); if (cur) cur.textContent = formatMoney(variant.price);
        const cmp = qs('.price__compare', el);
        if (cmp) { cmp.textContent = onSale ? formatMoney(variant.compare_at_price) : ''; cmp.hidden = !onSale; }
        const badge = qs('.price__badge', el);
        if (badge) {
          badge.hidden = !onSale;
          if (onSale) {
            const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
            badge.textContent = (badge.dataset.badgeText || '-[percent]%').replace('[percent]', String(pct));
          }
        }
      });
      qsa('[data-sticky-variant]', root).forEach((el) => { el.textContent = variant.title; });
      if (variant.featured_media) {
        const img = qs('.sticky-atc__media img', root);
        if (img && variant.featured_media.src) { img.removeAttribute('srcset'); img.src = variant.featured_media.src; }
        root.dispatchEvent(new CustomEvent('media:select', { detail: { id: variant.featured_media.id } }));
      }
      if (this.dataset.updateUrl === 'true' && !this.closest('.quick-add__content')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      const more = qs('.quick-add__more', root);
      if (more) { const u = new URL(more.href, window.location.origin); u.searchParams.set('variant', variant.id); more.href = u.pathname + u.search; }
      this.updateInventory(variant);
      this.refreshSection(variant);
    }
    updateInventory(variant) {
      const inv = qs('[data-inventory]', this.root);
      if (!inv) return;
      const raw = inv.dataset[`v${variant.id}`];
      const q = raw === undefined ? NaN : Number(raw);
      const low = Number(inv.dataset.threshold || 10);
      const show = !Number.isNaN(q) && variant.available && q > 0 && q <= low;
      inv.hidden = !show;
      const text = qs('[data-inventory-text]', inv);
      if (show && text) text.innerHTML = (inv.dataset.template || '').replace('__COUNT__', q);
    }
    // Server-rendered parts that depend on the variant (pickup availability) are refreshed via the Section Rendering API.
    async refreshSection(variant) {
      const root = this.root;
      const id = root.dataset?.sectionId;
      const url = root.dataset?.productUrl;
      if (!id || !url || !qs('[data-variant-sync]', root)) return;
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        const res = await fetch(`${url}?variant=${variant.id}&section_id=${id}`, { signal: this.abort.signal });
        const doc = parseHTML(await res.text());
        qsa('[data-variant-sync]', root).forEach((el) => {
          const fresh = qs(`[data-variant-sync="${el.dataset.variantSync}"]`, doc);
          if (fresh) el.replaceWith(fresh);
        });
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  // Product card colour swatches: show the colour's image and link the card to that variant.
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.card-swatch');
    if (!sw) return;
    e.preventDefault();
    const card = sw.closest('.product-card');
    if (!card) return;
    qsa('.card-swatch', card).forEach((b) => b.setAttribute('aria-pressed', String(b === sw)));
    const img = qs('.product-card__media .media:not(.media--cover) img', card);
    if (img && sw.dataset.image) {
      if (!img.dataset.originalSrc) img.dataset.originalSrc = img.currentSrc || img.src;
      img.removeAttribute('srcset');
      img.src = sw.dataset.image;
    }
    if (sw.dataset.variantUrl) qsa('a.product-card__link, a.product-card__title', card).forEach((a) => { a.href = sw.dataset.variantUrl; });
    const quick = qs('[data-quick-add]', card);
    if (quick && sw.dataset.variantId) quick.dataset.variantId = sw.dataset.variantId;
  });

  /* ---------------- Quantity input ---------------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.minus = qs('button[name="minus"]', this);
      qsa('button', this).forEach((b) => b.addEventListener('click', (e) => {
        if (this.closest('cart-items')) return;
        e.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = Number(this.input.min) || 1;
        const next = Number(this.input.value) + (b.name === 'plus' ? step : -step);
        this.input.value = Math.max(min, next);
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      this.input?.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      if (!this.minus || !this.input) return;
      this.minus.classList.toggle('is-min', Number(this.input.value) <= (Number(this.input.min) || 1));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------------- Product gallery (thumbs + stacked media; swipe track + counter below 990px) ---------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = qs('.product-gallery__main', this);
      this.thumbs = qsa('.product-gallery__thumb', this);
      this.items = qsa('.product-gallery__item', this);
      this.counter = qs('[data-gallery-counter]', this);
      this.slider = qs('carousel-slider', this);
      const root = this.closest('[data-product-root]');
      if (!this.main) return;
      if (this.slider) {
        root?.addEventListener('media:select', (e) => {
          const i = this.items.findIndex((it) => it.dataset.mediaId === String(e.detail.id));
          if (i >= 0) this.slider.goTo?.(i);
        });
        return;
      }
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.select(t.dataset.mediaId, true)));
      root?.addEventListener('media:select', (e) => this.select(String(e.detail.id), true, true));
      // Single-image layouts: arrows and keyboard step through the media.
      this.single = this.classList.contains('product-gallery--single');
      const step = (d) => {
        const cur = Math.max(0, this.items.findIndex((it) => it.classList.contains('is-active')));
        const next = this.items[(cur + d + this.items.length) % this.items.length];
        if (next) this.select(next.dataset.mediaId, true);
      };
      this.addEventListener('lightbox:closed', (e) => {
        const zoom = qsa('.product-gallery__zoom', this)[e.detail.index];
        const item = zoom?.closest('.product-gallery__item');
        if (!item) return;
        if (this.isSingle()) this.select(item.dataset.mediaId, false);
        const host = zoom.closest('product-gallery')?.querySelector('theme-dialog');
        if (host) host.opener = zoom;
      });
      qs('[data-gallery-prev]', this)?.addEventListener('click', () => step(-1));
      qs('[data-gallery-next]', this)?.addEventListener('click', () => step(1));
      if (this.single) {
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { if (this.isSingle()) { e.preventDefault(); step(e.key === 'ArrowLeft' ? -1 : 1); } }
        });
      }
      this.main.addEventListener('scroll', () => this.onTrackScroll(), { passive: true });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          if (this.isRow() || this.isSingle() || Date.now() < (this.lockUntil || 0)) return;
          entries.forEach((en) => { if (en.isIntersecting) this.mark(en.target.dataset.mediaId); });
        }, { rootMargin: '-45% 0px -45% 0px' });
        this.items.forEach((i) => this.io.observe(i));
      }
    }
    isRow() {
      const o = getComputedStyle(this.main).overflowX;
      return o === 'auto' || o === 'scroll';
    }
    // Desktop thumbnail layouts: only the active image is shown.
    isSingle() {
      return this.single && !this.isRow();
    }
    onTrackScroll() {
      if (!this.isRow()) return;
      const w = this.items[0]?.offsetWidth || this.main.clientWidth;
      const gap = parseFloat(getComputedStyle(this.main).columnGap) || 0;
      const i = Math.min(this.items.length - 1, Math.max(0, Math.round(this.main.scrollLeft / (w + gap))));
      if (this.items[i]) this.mark(this.items[i].dataset.mediaId);
    }
    mark(id) {
      const prev = this.items.findIndex((it) => it.classList.contains('is-active'));
      const target = this.items.findIndex((it) => it.dataset.mediaId === id);
      if (prev >= 0 && target >= 0) this.classList.toggle('is-back', target < prev);
      let index = 0;
      this.items.forEach((it, i) => { const on = it.dataset.mediaId === id; it.classList.toggle('is-active', on); if (on) index = i; });
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t.dataset.mediaId === id)));
      if (this.counter) this.counter.textContent = index + 1;
    }
    select(id, scroll, fromVariant = false) {
      const item = this.items.find((i) => i.dataset.mediaId === id);
      if (!item) return;
      this.mark(id);
      if (!scroll) return;
      const smooth = reduceMotion ? 'auto' : 'smooth';
      if (this.isRow()) {
        this.main.scrollTo({ left: item.offsetLeft - this.main.offsetLeft, behavior: smooth });
        return;
      }
      if (this.isSingle()) {
        // Keep the active thumbnail in view inside its own strip, without moving the page.
        const thumb = this.thumbs.find((t) => t.dataset.mediaId === id);
        const strip = thumb?.closest('.product-gallery__thumbs');
        if (thumb && strip && strip.scrollWidth > strip.clientWidth) strip.scrollTo({ left: thumb.offsetLeft - strip.offsetLeft - strip.clientWidth / 2 + thumb.offsetWidth / 2, behavior: smooth });
        if (fromVariant) {
          const r = this.getBoundingClientRect();
          if (r.top < 0 || r.top > window.innerHeight * 0.6) window.scrollTo({ top: r.top + window.scrollY - ((document.querySelector('sticky-header')?.offsetHeight || 0) + 30), behavior: smooth });
        }
        return;
      }
      const offset = (document.querySelector('sticky-header')?.offsetHeight || 0) + 30;
      const r = item.getBoundingClientRect();
      if (fromVariant && r.top >= offset - 2 && r.bottom <= window.innerHeight) return;
      this.lockUntil = Date.now() + 900;
      window.scrollTo({ top: r.top + window.scrollY - offset, behavior: smooth });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------------- Sticky add to cart ---------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.watch);
      if (!target || !('IntersectionObserver' in window)) return;
      const footer = qs('.footer') || qs('footer');
      let pastForm = false; let atFooter = false;
      const sync = () => {
        const visible = pastForm && !atFooter;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        qsa('button, a', this).forEach((el) => { el.tabIndex = visible ? 0 : -1; });
      };
      new IntersectionObserver(([e]) => { pastForm = !e.isIntersecting && e.boundingClientRect.top < 0; sync(); }).observe(target);
      if (footer) new IntersectionObserver(([e]) => { atFooter = e.isIntersecting; sync(); }, { rootMargin: '0px 0px -15% 0px' }).observe(footer);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------------- Predictive search ---------------- */
  // >=1200px the dialog anchors a pill input over the header search field and drops a results panel below the header;
  // smaller screens get a full-screen sheet.
  const mqSearchAnchor = window.matchMedia('(min-width: 1200px)');
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      this.clearBtn = qs('[data-search-clear]', this);
      this.defaultHTML = this.results?.innerHTML || '';
      this.host = this.closest('theme-dialog');
      this.box = this.closest('dialog');
      let t;
      this.input?.addEventListener('input', () => { this.syncClear(); clearTimeout(t); t = setTimeout(() => this.search(), 220); });
      this.clearBtn?.addEventListener('click', () => { this.input.value = ''; this.syncClear(); this.search(); this.input.focus(); });
      this.host?.addEventListener('dialog:open', () => { this.position(); setTimeout(() => this.input?.focus(), 60); });
      window.addEventListener('resize', () => { if (this.box?.open) this.position(); });
      this.syncClear();
    }
    syncClear() { if (this.clearBtn) this.clearBtn.hidden = !this.input?.value; }
    position() {
      if (!this.box) return;
      const anchor = qsa('.header__search-inline').find((el) => el.getBoundingClientRect().width > 0);
      const bar = anchor?.closest('.header__inner') || anchor?.closest('header');
      if (!anchor || !bar || !mqSearchAnchor.matches) { this.box.classList.remove('is-anchored'); return; }
      const a = anchor.getBoundingClientRect();
      const h = bar.getBoundingClientRect();
      const cs = getComputedStyle(bar);
      const right = Math.max(16, window.innerWidth - a.right - 12);
      const st = this.box.style;
      st.setProperty('--sb-top', `${Math.round(h.top + (h.height - 48) / 2)}px`);
      st.setProperty('--sb-left', `${Math.round(a.left - 16)}px`);
      st.setProperty('--sb-right', `${Math.round(right)}px`);
      st.setProperty('--sb-panel-top', `${Math.round(h.bottom)}px`);
      st.setProperty('--sb-bg', cs.backgroundColor);
      st.setProperty('--sb-fg', cs.color);
      this.box.classList.add('is-anchored');
    }
    async search() {
      const q = this.input.value.trim();
      this.classList.toggle('has-query', !!q);
      if (!q) { this.abort?.abort(); this.results.innerHTML = this.defaultHTML; return; }
      this.abort?.abort();
      this.abort = new AbortController();
      this.setAttribute('aria-busy', 'true');
      try {
        const params = new URLSearchParams({ q, section_id: 'predictive-search', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][prefix]': 'last' });
        const res = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`, { signal: this.abort.signal });
        if (!res.ok) throw new Error(`Search failed (${res.status})`);
        const fresh = qs('#shopify-section-predictive-search', parseHTML(await res.text()));
        if (this.input.value.trim() === q) this.results.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------------- Before/after comparison slider ---------------- */
  class CompareSlider extends HTMLElement {
    connectedCallback() {
      this.range = qs('input[type="range"]', this);
      this.range?.addEventListener('input', () => this.style.setProperty('--compare', `${this.range.value}%`));
    }
  }
  customElements.define('compare-slider', CompareSlider);

  /* ---------------- Hover-switch list (collection highlight) ---------------- */
  class HoverSwitch extends HTMLElement {
    connectedCallback() {
      this.triggers = qsa('[data-switch-index]', this);
      this.targets = qsa('[data-switch-target]', this);
      const activate = (i) => {
        this.triggers.forEach((t) => t.classList.toggle('is-active', t.dataset.switchIndex === i));
        this.targets.forEach((t) => t.classList.toggle('is-active', t.dataset.switchTarget === i));
      };
      this.triggers.forEach((t) => {
        t.addEventListener('mouseenter', () => activate(t.dataset.switchIndex));
        t.addEventListener('focus', () => activate(t.dataset.switchIndex));
      });
    }
  }
  customElements.define('hover-switch', HoverSwitch);

  /* ---------------- Progress timeline (scroll-linked) ---------------- */
  class ProgressTimeline extends HTMLElement {
    connectedCallback() {
      this.steps = qsa('[data-step]', this);
      this.images = qsa('[data-step-image]', this);
      this.line = qs('.timeline__line-fill', this);
      this.onScroll = () => {
        const rect = qs('.timeline__steps', this)?.getBoundingClientRect();
        if (!rect) return;
        const mid = window.innerHeight * 0.55;
        const p = Math.min(1, Math.max(0, (mid - rect.top) / rect.height));
        if (this.line) this.line.style.transform = `scaleY(${p})`;
        let active = 0;
        this.steps.forEach((s, i) => { if (s.getBoundingClientRect().top < mid) active = i; });
        this.steps.forEach((s, i) => s.classList.toggle('is-active', i <= active));
        this.images.forEach((img, i) => img.classList.toggle('is-active', i === active));
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.define('progress-timeline', ProgressTimeline);

  /* ---------------- Scrolling layers (stacked cards scale down while next pins over) ---------------- */
  class ScrollingLayers extends HTMLElement {
    connectedCallback() {
      this.layers = qsa('.layers__item', this);
      if (reduceMotion) return;
      this.onScroll = () => {
        this.layers.forEach((layer, i) => {
          const next = this.layers[i + 1];
          if (!next) return;
          const r = next.getBoundingClientRect();
          const top = parseFloat(getComputedStyle(next).top) || 0;
          const h = layer.offsetHeight;
          const p = Math.min(1, Math.max(0, 1 - (r.top - top) / h));
          layer.style.setProperty('--layer-progress', p.toFixed(3));
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() { if (this.onScroll) window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.define('scrolling-layers', ScrollingLayers);

  /* ---------------- Newsletter popup ---------------- */
  class NewsletterPopup extends ThemeDialog {
    connectedCallback() {
      super.connectedCallback();
      if (window.Shopify?.designMode) return;
      const key = 'pr-newsletter-dismissed';
      if (localStorage.getItem(key) && !window.location.search.includes('customer_posted')) return;
      const delay = (Number(this.dataset.delay) || 5) * 1000;
      setTimeout(() => this.open(), window.location.search.includes('customer_posted') ? 0 : delay);
      this.addEventListener('dialog:close', () => localStorage.setItem(key, String(Date.now())));
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);

  /* ---------------- Theme editor hooks ---------------- */
  document.addEventListener('shopify:section:select', (e) => {
    const popup = e.target.querySelector('newsletter-popup');
    popup?.open?.();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    e.target.querySelector('newsletter-popup')?.close?.();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('carousel-slider');
    if (slide) {
      const idx = Array.from(qs('.carousel__track', slide).children).indexOf(e.target.closest('.carousel__track > *'));
      if (idx >= 0) slide.goTo(idx);
    }
    const tab = e.target.closest('[role="tabpanel"]');
    const tabs = e.target.closest('tabs-component');
    if (tab && tabs) tabs.select(tabs.panels.indexOf(tab));
  });

  /* ---------------- Share button ---------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    const url = btn.dataset.share || window.location.href;
    if (navigator.share) { try { await navigator.share({ url, title: document.title }); } catch (_) { /* dismissed */ } return; }
    await navigator.clipboard?.writeText(url);
    const label = qs('[data-share-label]', btn);
    if (label) { const t = label.textContent; label.textContent = btn.dataset.copied || 'Link copied'; setTimeout(() => { label.textContent = t; }, 2000); }
  });

  /* ---------------- Collection filters (form → section re-render) ---------------- */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      let t;
      this.form.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => this.submit(), 400); });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
    }
    async submit() {
      const params = new URLSearchParams(new FormData(this.form));
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      const url = `${window.location.pathname}?${params.toString()}`;
      const section = this.closest('[data-section-id]');
      const id = section?.dataset.sectionId;
      if (!id) { window.location.href = url; return; }
      section.setAttribute('aria-busy', 'true');
      const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${id}`);
      const html = await res.text();
      const fresh = qs(`[data-section-id="${id}"]`, parseHTML(html));
      if (fresh) {
        const target = qs('[data-results]', section);
        const freshResults = qs('[data-results]', fresh);
        if (target && freshResults) target.replaceWith(freshResults);
        qsa('[data-facet-sync]', fresh).forEach((el) => { const cur = qs(`[data-facet-sync="${el.dataset.facetSync}"]`, section); if (cur) cur.replaceWith(el); });
      }
      section.removeAttribute('aria-busy');
      window.history.replaceState({}, '', url);
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ---------------- Scroll progress pill for sideways-scrolling areas ---------------- */
  function updateScrollProgress(scroller) {
    const bar = document.getElementById(scroller.dataset.scrollProgressSource);
    if (!bar) return;
    const max = scroller.scrollWidth - scroller.clientWidth;
    bar.hidden = max <= 1;
    if (bar.hidden) return;
    const ratio = scroller.clientWidth / scroller.scrollWidth;
    const thumb = bar.firstElementChild;
    thumb.style.width = `${ratio * 100}%`;
    thumb.style.transform = `translateX(${(scroller.scrollLeft / max) * (1 / ratio - 1) * 100}%)`;
  }
  function initScrollProgress(root = document) {
    qsa('[data-scroll-progress-source]', root).forEach((scroller) => {
      if (!scroller.dataset.progressBound) {
        scroller.dataset.progressBound = 'true';
        scroller.addEventListener('scroll', () => updateScrollProgress(scroller), { passive: true });
      }
      updateScrollProgress(scroller);
    });
  }
  document.addEventListener('DOMContentLoaded', () => initScrollProgress());
  document.addEventListener('shopify:section:load', (e) => initScrollProgress(e.target));
  window.addEventListener('resize', () => qsa('[data-scroll-progress-source]').forEach(updateScrollProgress), { passive: true });

  document.addEventListener('change', (e) => {
    const note = e.target.closest('[data-cart-note][data-autosave]');
    if (note) { clearTimeout(noteTimer); saveCartNote(note.value); }
    const sort = e.target.closest('[data-sort-by]');
    if (sort) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      window.location.href = url.toString();
    }
    const layout = e.target.closest('[data-grid-toggle]');
    if (layout) document.querySelectorAll('[data-results-grid]').forEach((g) => { g.dataset.layout = layout.value; });
    const loc = e.target.closest('[data-localization-select]');
    if (loc) loc.form.submit();
  });
})();

/* Button hover animation: magnetic pull (Theme settings > Buttons, inputs and media) */
(function () {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function initMagnetic(root) {
    if (!document.body.classList.contains('btn-fx-magnetic')) return;
    if (reduced.matches || !window.matchMedia('(hover: hover)').matches) return;
    (root || document).querySelectorAll('.btn').forEach((btn) => {
      if (btn.dataset.magBound) return;
      btn.dataset.magBound = '1';
      btn.addEventListener('mousemove', (e) => {
        if (btn.getAttribute('aria-busy') === 'true') return;
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${(x * 0.18).toFixed(1)}px, ${(y * 0.3).toFixed(1)}px)`;
      });
      btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
    });
  }
  document.addEventListener('DOMContentLoaded', () => initMagnetic());
  document.addEventListener('shopify:section:load', (e) => initMagnetic(e.target));
})();

/* Image load-in: fade images softly as they finish loading (Theme settings > Animations) */
(function () {
  const enabled = document.documentElement.dataset.imgFade === 'on';
  function bind(img) {
    if (img.dataset.fadeBound) return;
    img.dataset.fadeBound = '1';
    const show = () => img.classList.add('img-in');
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('img-fade', 'img-in');
    } else {
      img.classList.add('img-fade');
      img.addEventListener('load', show, { once: true });
      img.addEventListener('error', show, { once: true });
    }
  }
  function initImgFade(root) {
    if (!enabled || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    (root || document).querySelectorAll('img').forEach(bind);
  }
  document.addEventListener('DOMContentLoaded', () => initImgFade());
  document.addEventListener('shopify:section:load', (e) => initImgFade(e.target));
})();
